import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    X,
    Loader2,
    CheckCircle2,
    Pencil,
    Trash2,
    FileText,
    Eye,
    Inbox,
    RefreshCw,
    AlertCircle,
    Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { useFinancialContext } from '../../../context/FinancialContext';
import { ReceiptModal } from './ReceiptModal';
import { formatJobNumber } from '../../../lib/docNumbers';
import { errorMessage } from '../../../lib/errors';

interface ReviewQueueModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Fired after any successful resolve/reject so parents can refresh ledger data.
    onResolved?: () => void;
}

// --- Reference rows -----------------------------------------------------------

interface CategoryRow {
    id: string;
    name: string;
    default_vat_rate: number;
    is_tax_deductible: boolean;
}

interface ActiveJobRow {
    id: string;
    job_number: number | null;
    title: string | null;
    customer_name: string | null;
    vehicle_reg: string | null;
    status: string | null;
}

// Open supplier returns a credit note can be matched against (same pending set
// as the Credits & Write-offs screen). A credit can only post against one of these.
const OPEN_RETURN_STATUSES = ['pending_collection', 'collected', 'credit_matched'];

interface OpenReturnRow {
    id: string;
    label: string;
}

// --- Unified queue item -------------------------------------------------------
// Two backend sources feed the drain:
//  1. ocr_review_queue rows (review_type credit_match / statement_credit_match)
//     — resolved via fn_resolve_review_item (confirm / edit-via-overrides / reject).
//  2. processing_queue rows stuck in needs_review / manual_entry (standard
//     expense receipts OCR couldn't auto-post) — posted via the canonical
//     fn_process_standard_expense RPC, or the queue row is marked rejected.
type ItemKind = 'credit_review' | 'expense_queue';

interface QueueItem {
    kind: ItemKind;
    id: string; // review_id (credit) OR processing_queue id (expense)
    createdAt: string;
    filePath: string | null;
    fileName: string | null;
    // credit_review specifics
    reviewType?: string;
    suggestedMatch?: any;
    // expense_queue specifics
    parsed?: any; // processing_queue.result
    queueStatus?: string;
    confidence?: number | null;
}

// Editable form state for the currently-expanded item.
interface EditState {
    // shared
    supplier: string;
    docDate: string; // yyyy-mm-dd
    // expense
    categoryName: string;
    gross: string;
    vat: string;
    jobId: string;
    // credit
    returnId: string;
    creditRef: string;
    rejectReason: string;
}

const emptyEdit: EditState = {
    supplier: '',
    docDate: '',
    categoryName: '',
    gross: '',
    vat: '',
    jobId: '',
    returnId: '',
    creditRef: '',
    rejectReason: '',
};

function toDateInput(v: unknown): string {
    if (!v || typeof v !== 'string') return '';
    // Accept ISO timestamps or plain dates; keep only the yyyy-mm-dd head.
    const head = v.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : '';
}

function num(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

export function ReviewQueueModal({ isOpen, onClose, onResolved }: ReviewQueueModalProps) {
    const { activeOrgId, molded } = useVertical();
    const { is_vat_registered } = useFinancialContext();

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<QueueItem[]>([]);
    const [categories, setCategories] = useState<CategoryRow[]>([]);
    const [activeJobs, setActiveJobs] = useState<ActiveJobRow[]>([]);
    const [openReturns, setOpenReturns] = useState<OpenReturnRow[]>([]);

    // Which item is expanded for editing, plus a working copy of its fields.
    const [editingId, setEditingId] = useState<string | null>(null);
    const [edit, setEdit] = useState<EditState>(emptyEdit);

    // Per-item busy flag (id -> true) so buttons disable while an action runs.
    const [busyId, setBusyId] = useState<string | null>(null);

    // Document preview (reuses the shared ReceiptModal).
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewVendor, setPreviewVendor] = useState('');

    const clientLabel = molded.jargon.client_label || 'Customer';

    // --- Data loading ---------------------------------------------------------

    const fetchItems = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        try {
            const [creditRes, queueRes] = await Promise.all([
                supabase
                    .from('ocr_review_queue')
                    .select('id, review_type, suggested_match, status, created_at')
                    .eq('org_id', activeOrgId)
                    .eq('status', 'pending')
                    .order('created_at', { ascending: false }),
                supabase
                    .from('processing_queue')
                    .select('id, file_path, file_name, status, confidence_score, result, created_at')
                    .eq('org_id', activeOrgId)
                    .in('status', ['needs_review', 'manual_entry'])
                    .order('created_at', { ascending: false }),
            ]);

            if (creditRes.error) console.error('ReviewQueue: ocr_review_queue load failed', creditRes.error);
            if (queueRes.error) console.error('ReviewQueue: processing_queue load failed', queueRes.error);

            const creditItems: QueueItem[] = (creditRes.data ?? []).map((r: any) => ({
                kind: 'credit_review',
                id: r.id,
                createdAt: r.created_at,
                reviewType: r.review_type,
                suggestedMatch: r.suggested_match ?? {},
                filePath: r.suggested_match?.file_path ?? null,
                fileName: r.suggested_match?.file_name ?? null,
            }));

            const expenseItems: QueueItem[] = (queueRes.data ?? []).map((r: any) => ({
                kind: 'expense_queue',
                id: r.id,
                createdAt: r.created_at,
                filePath: r.file_path ?? null,
                fileName: r.file_name ?? null,
                parsed: r.result ?? {},
                queueStatus: r.status,
                confidence: r.confidence_score,
            }));

            // Newest first across both sources.
            const merged = [...creditItems, ...expenseItems].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            setItems(merged);
        } finally {
            setLoading(false);
        }
    }, [activeOrgId]);

    useEffect(() => {
        if (!isOpen || !activeOrgId) return;
        fetchItems();
        // Close any stale edit/preview state on (re)open.
        setEditingId(null);
        setEdit(emptyEdit);
    }, [isOpen, activeOrgId, fetchItems]);

    // Reference data for the edit form (categories + linkable jobs).
    useEffect(() => {
        if (!isOpen || !activeOrgId) return;
        const load = async () => {
            const [catRes, jobRes, returnRes] = await Promise.all([
                supabase
                    .from('expense_categories')
                    .select('id, name, default_vat_rate, is_tax_deductible')
                    .order('name'),
                supabase
                    .from('jobs')
                    .select('id, job_number, title, status, customer:customers(full_name), vehicle:vehicles(vrm)')
                    .eq('org_id', activeOrgId)
                    .is('deleted_at', null)
                    .not('status', 'in', '(cancelled,draft)')
                    .order('created_at', { ascending: false })
                    .limit(100),
                supabase
                    .from('part_returns')
                    .select('id, part_name, supplier, cost_price, quantity, credit_amount, vrm, status')
                    .eq('org_id', activeOrgId)
                    .in('status', OPEN_RETURN_STATUSES)
                    .order('created_at', { ascending: false })
                    .limit(100),
            ]);
            setCategories((catRes.data ?? []) as CategoryRow[]);
            setActiveJobs(
                (jobRes.data ?? []).map((j: any) => ({
                    id: j.id,
                    job_number: j.job_number ?? null,
                    title: j.title,
                    status: j.status,
                    customer_name: j.customer?.full_name?.trim() || null,
                    vehicle_reg: j.vehicle?.vrm ?? null,
                }))
            );
            setOpenReturns(
                (returnRes.data ?? []).map((r: any) => {
                    const amount = num(r.credit_amount) || num(r.cost_price) * (num(r.quantity) || 1);
                    const parts = [
                        r.part_name || 'Part',
                        r.supplier || null,
                        amount ? `£${amount.toFixed(2)}` : null,
                        r.vrm || null,
                    ].filter(Boolean);
                    return { id: r.id, label: parts.join(' · ') };
                })
            );
        };
        load();
    }, [isOpen, activeOrgId]);

    // --- Document preview -----------------------------------------------------
    // Mint the signed URL directly against the expense-receipts bucket — the
    // same pattern LiveLedgerTab uses. RLS scopes it to the uploader.
    const viewDocument = useCallback(async (item: QueueItem) => {
        if (!item.filePath) {
            toast.error('No source document on file for this item.');
            return;
        }
        const loadingToast = toast.loading('Locating document...');
        try {
            if (item.filePath.startsWith('http')) {
                setPreviewUrl(item.filePath);
                setPreviewVendor(item.fileName || 'Source document');
                return;
            }
            const { data, error } = await supabase.storage
                .from('expense-receipts')
                .createSignedUrl(item.filePath, 300);
            if (error) throw error;
            if (data?.signedUrl) {
                setPreviewUrl(data.signedUrl);
                setPreviewVendor(item.fileName || 'Source document');
            } else {
                toast.error('Could not generate a document link.');
            }
        } catch (err) {
            console.error('ReviewQueue: signed URL failed', err);
            toast.error('Failed to open document.');
        } finally {
            toast.dismiss(loadingToast);
        }
    }, []);

    // --- Edit form seeding ----------------------------------------------------

    const beginEdit = (item: QueueItem) => {
        if (item.kind === 'credit_review') {
            const sm = item.suggestedMatch ?? {};
            const candidateReturn =
                sm.return_id ?? (Array.isArray(sm.candidate_return_ids) ? sm.candidate_return_ids[0] : '');
            // Fall back to the only open return when the AI found no candidate,
            // so a single-return match is one click.
            const defaultReturn =
                candidateReturn || (openReturns.length === 1 ? openReturns[0].id : '');
            setEdit({
                ...emptyEdit,
                supplier: sm.supplier ?? '',
                docDate: toDateInput(sm.doc_date),
                gross: sm.gross != null ? String(sm.gross) : sm.amount != null ? String(sm.amount) : '',
                returnId: defaultReturn ?? '',
                creditRef: sm.credit_ref ?? '',
            });
        } else {
            const parsed = item.parsed ?? {};
            const first = parsed.extracted_items?.[0] ?? {};
            const totalGross = (parsed.extracted_items ?? []).reduce(
                (sum: number, it: any) => sum + num(it.total_amount),
                0
            );
            const totalVat = (parsed.extracted_items ?? []).reduce(
                (sum: number, it: any) => sum + num(it.vat_amount),
                0
            );
            setEdit({
                ...emptyEdit,
                supplier: parsed.supplier_or_bank_name ?? '',
                docDate: toDateInput(parsed.document_date),
                categoryName: first.suggested_category_name ?? first.category ?? '',
                gross: totalGross ? String(totalGross) : '',
                vat: totalVat ? String(totalVat) : '',
                creditRef: parsed.invoice_or_account_number ?? '',
            });
        }
        setEditingId(item.id);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEdit(emptyEdit);
    };

    // --- Resolve actions ------------------------------------------------------

    const afterAction = (id: string) => {
        // Optimistically drop the resolved item; keep the modal open for the rest.
        setItems((prev) => prev.filter((i) => i.id !== id));
        if (editingId === id) cancelEdit();
        onResolved?.();
    };

    // CREDIT-MATCH: fn_resolve_review_item (confirm | reject) with p_overrides.
    const resolveCredit = async (item: QueueItem, action: 'confirm' | 'reject', withOverrides: boolean) => {
        setBusyId(item.id);
        try {
            let overrides: Record<string, unknown> | null = null;
            if (action === 'reject') {
                overrides = edit.rejectReason ? { reason: edit.rejectReason } : { reason: 'Rejected in review' };
            } else if (withOverrides) {
                // Only the keys fn_resolve_review_item reads for a credit confirm.
                overrides = {
                    return_id: edit.returnId || undefined,
                    gross: edit.gross ? Number(edit.gross) : undefined,
                    supplier: edit.supplier || undefined,
                    credit_ref: edit.creditRef || undefined,
                    doc_date: edit.docDate || undefined,
                };
            }

            const { data, error } = await supabase.rpc('fn_resolve_review_item', {
                p_review_id: item.id,
                p_action: action,
                p_overrides: overrides,
            });
            if (error) throw error;

            const status = (data as any)?.status;
            if (status === 'already_resolved') {
                toast('This item was already actioned.', { icon: 'ℹ️' });
            } else if (action === 'reject') {
                toast.success('Item rejected');
            } else {
                toast.success('Credit confirmed & posted');
            }
            afterAction(item.id);
        } catch (err) {
            console.error('ReviewQueue: resolveCredit failed', err);
            toast.error(errorMessage(err) || 'Could not resolve item');
        } finally {
            setBusyId(null);
        }
    };

    // CREDIT-MATCH dismiss: the credit was already settled elsewhere (e.g. the
    // user marked the return credited manually on Credits & Write-offs), so there
    // is nothing to post — just resolve the review so the prompt clears. Uses the
    // same reject path (which resolves without posting) with a clear reason.
    const dismissCreditAlreadyRecorded = async (item: QueueItem) => {
        setBusyId(item.id);
        try {
            const { data, error } = await supabase.rpc('fn_resolve_review_item', {
                p_review_id: item.id,
                p_action: 'reject',
                p_overrides: { reason: 'Already recorded manually (settled outside OCR)' },
            });
            if (error) throw error;
            if ((data as any)?.status === 'already_resolved') {
                toast('This item was already actioned.', { icon: 'ℹ️' });
            } else {
                toast.success('Cleared — already recorded');
            }
            afterAction(item.id);
        } catch (err) {
            console.error('ReviewQueue: dismissCreditAlreadyRecorded failed', err);
            toast.error(errorMessage(err) || 'Could not clear item');
        } finally {
            setBusyId(null);
        }
    };

    // EXPENSE QUEUE: post via canonical fn_process_standard_expense (confirm /
    // edit), or mark the queue row rejected (reject).
    const resolveExpense = async (item: QueueItem, action: 'confirm' | 'reject', withOverrides: boolean) => {
        setBusyId(item.id);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user?.id) throw new Error('Not signed in');

            if (action === 'reject') {
                const { error } = await supabase
                    .from('processing_queue')
                    .update({ status: 'rejected', processed_at: new Date().toISOString() })
                    .eq('id', item.id)
                    .eq('org_id', activeOrgId as string);
                if (error) throw error;
                toast.success('Receipt discarded');
                afterAction(item.id);
                return;
            }

            // Build p_parsed for fn_process_standard_expense from the extracted
            // result, applying the user's edits on a confirm-with-edits.
            const parsed = { ...(item.parsed ?? {}) };
            if (withOverrides) {
                const gross = edit.gross ? Number(edit.gross) : undefined;
                if (gross != null && (!Number.isFinite(gross) || gross <= 0)) {
                    throw new Error('Enter a positive gross amount');
                }
                const vat = edit.vat ? Number(edit.vat) : 0;
                if (gross != null && (vat < 0 || vat > gross)) {
                    throw new Error('VAT must be between 0 and the gross total');
                }
                if (!edit.categoryName) throw new Error('Pick a category');

                // Collapse to a single edited line item carrying the corrected
                // category NAME (contract: category fields hold the name).
                parsed.supplier_or_bank_name = edit.supplier || parsed.supplier_or_bank_name;
                if (edit.docDate) parsed.document_date = edit.docDate;
                if (edit.creditRef) parsed.invoice_or_account_number = edit.creditRef;
                parsed.extracted_items = [
                    {
                        merchant_or_description:
                            parsed.extracted_items?.[0]?.merchant_or_description ||
                            edit.supplier ||
                            'Reviewed expense',
                        suggested_category_name: edit.categoryName,
                        category: edit.categoryName,
                        total_amount: gross ?? 0,
                        vat_amount: vat,
                        date: edit.docDate || parsed.document_date,
                        transaction_type: 'expense',
                    },
                ];
            }

            // fn_process_standard_expense is service-role only — it is SECURITY
            // DEFINER over a caller-supplied org_id, so calling it from here was
            // both a 42501 (the lockdown migration revoked it, which is why
            // Confirm silently did nothing) and, if granted, a cross-tenant
            // write. fn_review_queue_post_expense takes the queue id alone and
            // derives org, file path and user server-side. It also closes the
            // queue row in the same transaction, so a failure after the ledger
            // write can no longer leave the receipt postable twice.
            const { error: rpcError } = await supabase.rpc('fn_review_queue_post_expense', {
                p_queue_id: item.id,
                p_parsed: parsed,
            });
            if (rpcError) throw rpcError;

            // Optional job link: the posting RPC doesn't take a job id,
            // so stamp it onto the freshly-posted header when the user set one.
            if (withOverrides && edit.jobId) {
                const { error: linkErr } = await supabase
                    .from('ledger_headers')
                    .update({ job_id: edit.jobId })
                    .eq('org_id', activeOrgId as string)
                    .eq('document_url', item.filePath as string)
                    .eq('transaction_type', 'expense');
                if (linkErr) console.error('ReviewQueue: job link stamp failed', linkErr);
            }

            toast.success('Expense filed');
            afterAction(item.id);
        } catch (err) {
            console.error('ReviewQueue: resolveExpense failed', err);
            toast.error(errorMessage(err) || 'Could not file expense');
        } finally {
            setBusyId(null);
        }
    };

    const confirm = (item: QueueItem, withOverrides: boolean) =>
        item.kind === 'credit_review'
            ? resolveCredit(item, 'confirm', withOverrides)
            : resolveExpense(item, 'confirm', withOverrides);

    const reject = (item: QueueItem) =>
        item.kind === 'credit_review'
            ? resolveCredit(item, 'reject', false)
            : resolveExpense(item, 'reject', false);

    const creditCount = useMemo(() => items.filter((i) => i.kind === 'credit_review').length, [items]);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-panel border border-line rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 border-b border-line bg-raised">
                        <div>
                            <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                                <Inbox className="w-5 h-5 text-accent" />
                                Review Queue
                            </h2>
                            <p className="text-sm text-fg-3 mt-1">
                                Confirm, correct, or reject documents the AI couldn't file automatically.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={fetchItems}
                                disabled={loading}
                                className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg disabled:opacity-50"
                                title="Refresh"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 flex-1 overflow-y-auto custom-scrollbar space-y-3">
                        {loading && items.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 text-fg-4">
                                <Loader2 className="w-6 h-6 animate-spin mb-3" />
                                <p className="text-sm">Loading review queue…</p>
                            </div>
                        )}

                        {!loading && items.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="p-4 bg-success/10 border border-success/20 rounded-full mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-success" />
                                </div>
                                <h3 className="text-lg font-semibold text-fg">All caught up</h3>
                                <p className="text-sm text-fg-3 mt-1 max-w-sm">
                                    Nothing is waiting for review. New items land here when OCR can't file a
                                    document with confidence.
                                </p>
                            </div>
                        )}

                        {items.map((item) => {
                            const isEditing = editingId === item.id;
                            const isBusy = busyId === item.id;
                            // A credit match with no pre-resolved return can't be
                            // one-click confirmed — the user must Edit and pick an
                            // open return first (or reject it).
                            const creditNeedsMatch =
                                item.kind === 'credit_review' &&
                                getCandidateReturns(item.suggestedMatch).length === 0;
                            return (
                                <div
                                    key={item.id}
                                    className="bg-card border border-line rounded-xl p-4"
                                >
                                    {/* Summary row */}
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-lg bg-raised flex items-center justify-center shrink-0">
                                                <FileText className="w-5 h-5 text-accent" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-semibold text-fg truncate max-w-[16rem]">
                                                        {item.kind === 'credit_review'
                                                            ? item.suggestedMatch?.supplier || 'Supplier credit'
                                                            : item.parsed?.supplier_or_bank_name || 'Unknown vendor'}
                                                    </p>
                                                    <span
                                                        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${
                                                            item.kind === 'credit_review'
                                                                ? 'bg-chart-4/15 text-chart-4 border border-chart-4/25'
                                                                : 'bg-gold-fill/15 text-gold border border-gold/25'
                                                        }`}
                                                    >
                                                        {item.kind === 'credit_review'
                                                            ? 'Credit match'
                                                            : item.queueStatus === 'manual_entry'
                                                              ? 'Manual entry'
                                                              : 'Needs review'}
                                                    </span>
                                                </div>
                                                <ItemSummary item={item} />
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => viewDocument(item)}
                                            disabled={!item.filePath}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-raised hover:bg-pressed disabled:opacity-40 disabled:cursor-not-allowed text-fg-2 hover:text-fg rounded-lg text-xs font-medium transition-colors border border-line-strong shrink-0"
                                            title={item.filePath ? 'Preview source document' : 'No document on file'}
                                        >
                                            <Eye className="w-3.5 h-3.5" />
                                            View
                                        </button>
                                    </div>

                                    {/* Edit form */}
                                    {isEditing && (
                                        <div className="mt-4 pt-4 border-t border-line space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                                            {item.kind === 'expense_queue' ? (
                                                <>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Field label="Vendor">
                                                            <input
                                                                type="text"
                                                                value={edit.supplier}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, supplier: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                        <Field label="Date">
                                                            <input
                                                                type="date"
                                                                value={edit.docDate}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, docDate: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                    </div>
                                                    <Field label="Category (HMRC SA103)">
                                                        <select
                                                            value={edit.categoryName}
                                                            onChange={(e) =>
                                                                setEdit((s) => ({ ...s, categoryName: e.target.value }))
                                                            }
                                                            className={inputCls}
                                                        >
                                                            <option value="">— Pick a category —</option>
                                                            {categories.map((c) => (
                                                                <option key={c.id} value={c.name}>
                                                                    {c.name}
                                                                    {c.is_tax_deductible ? '' : ' (non-deductible)'}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Field label="Gross total (£)">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={edit.gross}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, gross: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                        <Field
                                                            label={
                                                                is_vat_registered ? 'VAT (£)' : 'VAT (not registered)'
                                                            }
                                                        >
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={edit.vat}
                                                                disabled={!is_vat_registered}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, vat: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                    </div>
                                                    <Field
                                                        label={
                                                            <span className="flex items-center gap-1">
                                                                <Link2 className="w-3 h-3" /> Link to{' '}
                                                                {molded.jargon.action_label?.toLowerCase() || 'job'}{' '}
                                                                (optional)
                                                            </span>
                                                        }
                                                    >
                                                        <select
                                                            value={edit.jobId}
                                                            onChange={(e) =>
                                                                setEdit((s) => ({ ...s, jobId: e.target.value }))
                                                            }
                                                            className={inputCls}
                                                        >
                                                            <option value="">— No link —</option>
                                                            {activeJobs.map((job) => {
                                                                const num =
                                                                    job.job_number != null
                                                                        ? `${formatJobNumber(job.job_number)} · `
                                                                        : '';
                                                                const tag = job.vehicle_reg
                                                                    ? ` · ${job.vehicle_reg}`
                                                                    : '';
                                                                const customer =
                                                                    job.customer_name || `Unnamed ${clientLabel}`;
                                                                return (
                                                                    <option key={job.id} value={job.id}>
                                                                        {num}
                                                                        {customer}
                                                                        {tag}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </Field>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Field label="Supplier">
                                                            <input
                                                                type="text"
                                                                value={edit.supplier}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, supplier: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                        <Field label="Credit gross (£)">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={edit.gross}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, gross: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <Field label="Credit ref">
                                                            <input
                                                                type="text"
                                                                value={edit.creditRef}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, creditRef: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                        <Field label="Document date">
                                                            <input
                                                                type="date"
                                                                value={edit.docDate}
                                                                onChange={(e) =>
                                                                    setEdit((s) => ({ ...s, docDate: e.target.value }))
                                                                }
                                                                className={inputCls}
                                                            />
                                                        </Field>
                                                    </div>
                                                    <Field label="Matched return">
                                                        <select
                                                            value={edit.returnId}
                                                            onChange={(e) =>
                                                                setEdit((s) => ({ ...s, returnId: e.target.value }))
                                                            }
                                                            disabled={openReturns.length === 0}
                                                            className={inputCls}
                                                        >
                                                            <option value="">— Select return —</option>
                                                            {openReturns.map((r) => (
                                                                <option key={r.id} value={r.id}>
                                                                    {r.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </Field>
                                                    {openReturns.length === 0 ? (
                                                        <p className="text-[11px] text-gold/80">
                                                            No open return to match this credit against. If you already
                                                            recorded it (e.g. marked the return credited on Credits &amp;
                                                            Write-offs), close the editor and use “Already recorded” to
                                                            clear it. Otherwise log the return first, or reject this item.
                                                        </p>
                                                    ) : (
                                                        <p className="text-[11px] text-fg-4">
                                                            The credit will be posted against the selected return via the
                                                            supplier-credit ledger.
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Action bar */}
                                    <div className="mt-4 pt-3 border-t border-line/60 flex items-center justify-end gap-2 flex-wrap">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    onClick={cancelEdit}
                                                    disabled={isBusy}
                                                    className="px-3 py-1.5 bg-raised hover:bg-pressed disabled:opacity-50 text-fg-2 rounded-lg text-xs font-medium transition-colors border border-line-strong"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => confirm(item, true)}
                                                    disabled={
                                                        isBusy ||
                                                        (item.kind === 'credit_review' && !edit.returnId)
                                                    }
                                                    title={
                                                        item.kind === 'credit_review' && !edit.returnId
                                                            ? 'Select a matched return first'
                                                            : undefined
                                                    }
                                                    className="flex items-center gap-1.5 px-4 py-1.5 fd-btn-primary disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-xs font-semibold transition-all"
                                                >
                                                    {isBusy ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                    )}
                                                    Save & confirm
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {item.kind === 'credit_review' && (
                                                    <button
                                                        onClick={() => dismissCreditAlreadyRecorded(item)}
                                                        disabled={isBusy}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-raised hover:bg-pressed disabled:opacity-50 text-fg-2 rounded-lg text-xs font-medium transition-colors border border-line-strong"
                                                        title="I've already recorded this credit (e.g. marked the return credited manually) — clear this prompt without posting again"
                                                    >
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        Already recorded
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => reject(item)}
                                                    disabled={isBusy}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-raised hover:bg-danger/10 hover:text-danger hover:border-danger/30 disabled:opacity-50 text-fg-2 rounded-lg text-xs font-medium transition-colors border border-line-strong"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                    Reject
                                                </button>
                                                <button
                                                    onClick={() => beginEdit(item)}
                                                    disabled={isBusy}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-raised hover:bg-pressed disabled:opacity-50 text-fg-2 rounded-lg text-xs font-medium transition-colors border border-line-strong"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        creditNeedsMatch ? beginEdit(item) : confirm(item, false)
                                                    }
                                                    disabled={isBusy}
                                                    title={
                                                        creditNeedsMatch
                                                            ? 'Match this credit to an open return first'
                                                            : undefined
                                                    }
                                                    className="flex items-center gap-1.5 px-4 py-1.5 bg-success-fill hover:bg-success-fill disabled:opacity-50 text-on-fill rounded-lg text-xs font-semibold transition-colors shadow-lg shadow-success/20"
                                                >
                                                    {isBusy ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                    )}
                                                    {creditNeedsMatch ? 'Match & confirm' : 'Confirm'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    {items.length > 0 && (
                        <div className="px-6 py-4 border-t border-line bg-raised flex items-center justify-between">
                            <p className="text-xs text-fg-4 flex items-center gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5" />
                                {items.length} item{items.length === 1 ? '' : 's'} waiting
                                {creditCount > 0 ? ` · ${creditCount} credit` : ''}
                            </p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-raised hover:bg-pressed text-fg rounded-lg text-sm font-medium transition-colors border border-line-strong"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ReceiptModal
                url={previewUrl}
                vendor={previewVendor}
                onClose={() => setPreviewUrl(null)}
            />
        </>
    );
}

// --- Helpers ------------------------------------------------------------------

const inputCls =
    'w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors';

function getCandidateReturns(sm: any): string[] {
    if (!sm) return [];
    const ids = new Set<string>();
    if (sm.return_id) ids.add(sm.return_id);
    if (Array.isArray(sm.candidate_return_ids)) {
        sm.candidate_return_ids.forEach((id: string) => id && ids.add(id));
    }
    return Array.from(ids);
}

function ItemSummary({ item }: { item: QueueItem }) {
    if (item.kind === 'credit_review') {
        const sm = item.suggestedMatch ?? {};
        const gross = sm.gross ?? sm.amount;
        return (
            <p className="text-xs text-fg-4 mt-0.5">
                {gross != null ? `£${num(gross).toFixed(2)}` : 'No amount'}
                {sm.credit_ref ? ` · ${sm.credit_ref}` : ''}
                {sm.doc_date ? ` · ${toDateInput(sm.doc_date)}` : ''}
            </p>
        );
    }
    const parsed = item.parsed ?? {};
    const totalGross = (parsed.extracted_items ?? []).reduce(
        (sum: number, it: any) => sum + num(it.total_amount),
        0
    );
    const cat = parsed.extracted_items?.[0]?.suggested_category_name;
    return (
        <p className="text-xs text-fg-4 mt-0.5">
            {totalGross ? `£${totalGross.toFixed(2)}` : 'No amount'}
            {cat ? ` · ${cat}` : ''}
            {parsed.document_date ? ` · ${toDateInput(parsed.document_date)}` : ''}
            {item.fileName ? ` · ${item.fileName}` : ''}
        </p>
    );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
    return (
        <label className="block">
            <span className="text-xs font-medium text-fg-3 uppercase tracking-wider mb-1.5 block">
                {label}
            </span>
            {children}
        </label>
    );
}
